import {useAccessToken, useIsBankUser, useIsUnitUser} from "../../services/auth"
import {AllowedProduct, AppVariantName, CheckDepositScannerVendor, Org, updateOrg} from "../../resources/org"
import React, {ButtonHTMLAttributes, useEffect, useState} from "react"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {EditMode, ErrorDocument, RiskRate} from "../../resources/common"
import {useToasts} from "react-toast-notifications"
import {OrgAttributes} from "./OrgAttributes"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {faEdit, faWindowClose} from "@fortawesome/free-solid-svg-icons"
import OrgAccountsSummary from "../../components/OrgAccountsSummary/OrgAccountsSummary"
import OrgAndCustomerAccountsSummary from "../../components/OrgAccountsSummary/OrgAndCustomerAccountsSummary"
import InterestAndDepositFeeSummary from "../../components/InterestAndDepositFeeSummary/InterestAndDepositFeeSummary"
import EndCustomerDepositSummary from "../../components/EndCustomerDepositSummary/EndCustomerDepositSummary"
import {Customer} from "../../resources/customer"
import {AsyncResultIdleRequestState} from "../../types/asyncResultIdle"

const editFormId = "edit-org-overview"

interface OrgOverviewProps {
	org: Org
	currentDefaultBankName?: string
	customers: Customer[]
	bankNames?: string[]
	refresh: () => void
}
export default function OrgOverview({org, currentDefaultBankName, customers, bankNames, refresh}: OrgOverviewProps) {
	const accessToken = useAccessToken()
	const {addToast} = useToasts()

	const [state, update] = useAsyncResultIdle(updateOrg)
	const [customerId, setCustomerId] = useState<string | null>(
		org.attributes.customerId ? org.attributes.customerId.toString() : ""
	)
	const [verificationServiceId, setVerificationServiceId] = useState<string>(
		org.attributes.verificationServiceId ? org.attributes.verificationServiceId : ""
	)
	const [supportWidgetId, setSupportWidgetId] = useState<string | undefined | null>(org.attributes.supportWidgetId)
	const [freshchatSupportWidgetId, setFreshchatSupportWidgetId] = useState<string | undefined | null>(
		org.attributes.freshchatSupportWidgetId
	)
	const [orgInterchangeShare, setOrgInterchangeShare] = useState<number>(org.attributes.orgInterchangeShare)
	const [defaultCustomerRiskRate, setDefaultCustomerRiskRate] = useState<RiskRate>(
		org.attributes.defaultCustomerRiskRate
	)

	const [selectedDefaultBankName, setSelectedDefaultBankName] = useState<string | undefined>(currentDefaultBankName)

	const [enableTwoFactorAuthentication, setEnableTwoFactorAuthentication] = useState<boolean>(
		org.attributes.enableTwoFactorAuthentication
	)
	const [enableApplePushProvisioning, setEnableApplePushProvisioning] = useState<boolean>(
		org.attributes.enableApplePushProvisioning ?? false
	)
	const [enableGooglePushProvisioning, setEnableGooglePushProvisioning] = useState<boolean>(
		org.attributes.enableGooglePushProvisioning ?? false
	)
	const [isEditing, setIsEditing] = useState(false)

	const [name, setName] = useState(org.attributes.name)

	const [allowedProducts, setAllowedProducts] = useState<AllowedProduct[]>(org.attributes.allowedProducts ?? [])

	const [checkDepositScannerVendor, setCheckDepositScannerVendor] = useState<CheckDepositScannerVendor>(
		org.attributes.checkDepositScannerVendor || CheckDepositScannerVendor.Mitek
	)

	const [brandName, setBrandName] = useState<string | undefined>(org.attributes.brandName)

	const [appVariantName, setAppVariantName] = useState<AppVariantName>(
		org.attributes.appVariantName ?? AppVariantName.Balance
	)

	useEffect(() => {
		if (state.isOk()) {
			addToast("Org Updated Successfully", {appearance: "success"})
			setIsEditing(false)
			refresh()
		}
	}, [state])

	const submit = () => {
		update(
			accessToken,
			org.id,
			customerId != "" ? customerId : null,
			defaultCustomerRiskRate,
			verificationServiceId != "" ? verificationServiceId : undefined,
			name,
			enableTwoFactorAuthentication,
			selectedDefaultBankName !== currentDefaultBankName ? selectedDefaultBankName : undefined,
			supportWidgetId,
			freshchatSupportWidgetId,
			allowedProducts,
			checkDepositScannerVendor,
			brandName,
			enableApplePushProvisioning,
			enableGooglePushProvisioning,
			appVariantName
		)
	}

	const disabled = state.match(
		() => false,
		() => true,
		(_) => true,
		(_) => false
	)
	const isBankUser = useIsBankUser()
	const isUnitUser = useIsUnitUser()
	return (
		<>
			<div className="columns">
				<div className="column">
					<div className="buttons">
						{!isBankUser && (
							<button className="button is-light" onClick={() => (!isEditing ? setIsEditing(true) : refresh())}>
								<span>{isEditing ? "Cancel" : "Edit"}</span>
								<span className="icon">
									<FontAwesomeIcon icon={isEditing ? faWindowClose : faEdit} />
								</span>
							</button>
						)}
					</div>
				</div>
			</div>
			{!isBankUser && (
				<div className="columns">
					<div className="column">
						<form
							id={editFormId}
							onSubmit={(e) => {
								e.preventDefault()
								submit()
							}}
						>
							<fieldset disabled={disabled}>
								<OrgAttributes
									editMode={isEditing ? EditMode.EDIT : EditMode.VIEW}
									name={name}
									setName={setName}
									customerId={customerId}
									setCustomerId={setCustomerId}
									customers={customers}
									bankNames={bankNames}
									defaultBankName={selectedDefaultBankName}
									setDefaultBankName={setSelectedDefaultBankName}
									setOrgInterchangeShare={setOrgInterchangeShare}
									orgInterchangeShare={orgInterchangeShare}
									defaultCustomerRiskRate={defaultCustomerRiskRate}
									setDefaultCustomerRiskRate={setDefaultCustomerRiskRate}
									twilioVerificationServiceId={verificationServiceId}
									setTwilioVerificationServiceId={setVerificationServiceId}
									enableTwoFactorAuthentication={enableTwoFactorAuthentication}
									setEnableTwoFactorAuthentication={setEnableTwoFactorAuthentication}
									enableApplePushProvisioning={enableApplePushProvisioning}
									setEnableApplePushProvisioning={setEnableApplePushProvisioning}
									enableGooglePushProvisioning={enableGooglePushProvisioning}
									setEnableGooglePushProvisioning={setEnableGooglePushProvisioning}
									supportWidgetId={supportWidgetId}
									setSupportWidgetId={setSupportWidgetId}
									freshchatSupportWidgetId={freshchatSupportWidgetId}
									setFreshchatSupportWidgetId={setFreshchatSupportWidgetId}
									allowedProducts={allowedProducts}
									setAllowedProducts={setAllowedProducts}
									checkDepositScannerVendor={checkDepositScannerVendor}
									setCheckDepositScannerVendor={setCheckDepositScannerVendor}
									brandName={brandName}
									setBrandName={setBrandName}
									appVariantName={appVariantName}
									setAppVariantName={setAppVariantName}
								/>
							</fieldset>
						</form>
					</div>
				</div>
			)}
			<div className="columns org-overview-section">
				<div className="column">
					{org.attributes.customerId ? (
						<OrgAndCustomerAccountsSummary
							orgId={org.id}
							customerId={org.attributes.customerId.toString()}
							customerAccountsAccessToken={accessToken}
							showIcon
						/>
					) : (
						<OrgAccountsSummary orgId={org.id} accessToken={accessToken} showIcon />
					)}
				</div>
			</div>
			{isBankUser && (
				<div className="columns org-overview-section">
					<div className="column is-flex is-flex-direction-column">
						<EndCustomerDepositSummary orgId={org.id} accessToken={accessToken} />
					</div>
					<div className="column">
						<InterestAndDepositFeeSummary orgId={org.id} accessToken={accessToken} />
					</div>
				</div>
			)}
			{isUnitUser && (
				<div className="columns org-overview-section">
					<div className="column is-flex is-flex-direction-column">
						<EndCustomerDepositSummary orgId={org.id} accessToken={accessToken} />
					</div>
				</div>
			)}
			{!isBankUser && isEditing ? (
				<div className="columns">
					<div className="column">
						<div className="card">
							<Footer state={state} disabled={disabled} />
						</div>
					</div>
				</div>
			) : null}
		</>
	)
}

function Footer({state, disabled}: {state: AsyncResultIdleRequestState<Org, ErrorDocument>; disabled: boolean}) {
	const baseButtonProps: ButtonHTMLAttributes<HTMLButtonElement> = {
		form: editFormId,
		type: "submit",
		disabled,
	}

	return (
		<div className="card-content">
			{state.match(
				() => (
					<button {...baseButtonProps} className="button is-success">
						Update
					</button>
				),
				() => (
					<button {...baseButtonProps} className="button is-success is-loading">
						Updating
					</button>
				),
				(_) => null,
				(err) => (
					<>
						<div className="has-text-danger">{err.errors[0].title}</div>
						<button {...baseButtonProps} className="button is-success">
							Create
						</button>
					</>
				)
			)}
		</div>
	)
}
